import { useMutation, useQuery } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import PropTypes from 'prop-types';
import { createContext, useMemo, useState, useEffect } from 'react';
import { useSessionContext } from '@/auth/useSessionContext';
import { getSubscription } from '@/services/subscription';
import { queryClient } from '@/App';
import { getSubscriptionMetricBreakdown } from '@/utils/subscription';
import {
  DEFAULT_PRODUCT_CURRENCY_CODE,
  DOLARIZATION_SUPPORTED_CURRENCIES,
} from '@/utils/currency';
import { hasMultiCurrency } from '@/utils/multicurrency';
import useTableFilter from '../hooks/useTableFilter';
import { exportSuscriptionsService, cancelSybscription } from '../services/subscriptions';
import { OrderCoproductionType } from './OrdersContext';

// Espelha `SubscriptionStatus` de gateway/enums.py. Status que falta aqui nao some da tela de
// forma visivel: `SubscriptionsTable` resolve o label por lookup e cai no `|| '-'`, entao a
// assinatura aparece listada com um traco no lugar do estado. `inactive`, `paused` e `late`
// faltavam, e sozinhos respondem por 30% da base -- 231.250 em `inactive`.
export const SubscriptionStatus = {
  canceled: { value: 'canceled', label: 'Cancelado', color: 'error' },
  active: { value: 'active', label: 'Ativo', color: 'success' },
  expired: { value: 'expired', label: 'Expirado', color: 'error' },
  trial: { value: 'trial', label: 'Analisando', color: 'warning' },
  inactive: { value: 'inactive', label: 'Inativa', color: 'default' },
  paused: { value: 'paused', label: 'Pausada', color: 'info' },
  late: { value: 'late', label: 'Em atraso', color: 'warning' },
};

export const SubscriptionPaymentStatus = {
  canceled: { value: 'canceled', label: 'Cancelado', color: 'error' },
  active: { value: 'active', label: 'Ativo', color: 'success' },
  expired: { value: 'expired', label: 'Expirado', color: 'error' },
  trial: { value: 'trial', label: 'Analisando', color: 'warning' },
  processing: { value: 'processing', label: 'Processando', color: 'info' },
  authorized: { value: 'authorized', label: 'Autorizado', color: 'success' },
  paid: { value: 'paid', label: 'Pago', color: 'success' },
  refunded: { value: 'refunded', label: 'Reembolsado', color: 'info' },
  refund_requested: { value: 'refund_requested', label: 'Reembolso Solicitado', color: 'warning' },
  waiting_payment: { value: 'waiting_payment', label: 'Aguardando Pagamento', color: 'warning' },
  refused: { value: 'refused', label: 'Recusado', color: 'error' },
  chargedback: { value: 'chargedback', label: 'Chargeback', color: 'error' },
  in_protest: { value: 'in_protest', label: 'Em Protesto', color: 'warning' },
  partially_paid: { value: 'partially_paid', label: 'Pago Parcialmente', color: 'warning' },
  prechargeback: { value: 'prechargeback', label: 'Pré-Chargeback', color: 'warning' },
  scheduled: { value: 'scheduled', label: 'Agendado', color: 'info' },
  retrying: { value: 'retrying', label: 'Tentando Novamente', color: 'warning' },
  MED: { value: 'MED', label: 'Médio Risco', color: 'warning' },
  blocked: { value: 'blocked', label: 'Bloqueado', color: 'error' },
};

export const SubscriptionPaymentMethod = {
  pix: {
    value: 'pix',
    label: 'Pix',
    icon: 'simple-icons:pix',
  },
  pix_auto: {
    value: 'pix_auto',
    label: 'PIX Automático',
    icon: 'simple-icons:pix',
  },
  credit_card: {
    value: 'credit_card',
    label: 'Cartão de crédito',
    icon: 'ic:round-credit-card',
  },
  boleto: {
    value: 'boleto',
    label: 'Boleto',
    icon: 'ic:outline-barcode',
  },
  paypal_wallet: {
    value: 'paypal_wallet',
    label: 'PayPal',
    icon: 'simple-icons:paypal',
  },
};

export const PERIODS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  CUSTOM: 'custom',
  ALL_TIME: 'all_time',
};

export const SubscriptionContext = createContext({
  table: {},
  subscriptions: [],
  fetching: false,
  fetchingSubscription: false,
  refreshSubscriptions: async () => {},
  subscriptionsUpdatedAt: 0,
  plans: [],
  fetchingPlans: false,
  products: [],
  fetchingProducts: false,
  count: 0,
  cancel: () => {},
  canceling: false,
  exportSubscription: () => {},
  exporting: false,
  deleteSubscription: (id) => {},
  deleting: false,
  tab: 'active',
  setTab: () => {},
  activeCount: 0,
  mrrBreakdown: [],
  ltvBreakdown: [],
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
  hasMultiCurrencyFeature: false,
});

export const defaultFilter = {
  period: PERIODS.ALL_TIME,
  startDate: null,
  endDate: null,
  coproductionType: OrderCoproductionType.all.value,
  products: [],
  plans: [],
  paymentMethod: [],
  status: ['active'],
};

// Colunas que a API ordena (`SubscriptionMixin.ordering_fields` no backend). Plano, produto,
// membro e comissão não têm campo lá e ficam sem botão: ordenar só a página visível mentiria
// sobre as outras.
export const SUBSCRIPTION_ORDERING_FIELDS = {
  date: 'createdAt',
  renewalDate: 'next_payment_date',
  status: 'status',
};

export const resolveSubscriptionOrdering = ({ orderBy, order } = {}) => {
  const field = SUBSCRIPTION_ORDERING_FIELDS[orderBy];

  if (!field) return undefined;

  return order === 'desc' ? `-${field}` : field;
};

// Number(''), Number(null), and Number('  ') all coerce to 0, which would
// otherwise make blank/nullish ids look like valid numeric ids.
const isNumericId = (value) => {
  if (value === undefined || value === null) return false;
  const trimmed = String(value).trim();
  if (trimmed === '') return false;
  return !Number.isNaN(Number(trimmed));
};

// Compares two ids numerically when both look like numbers, falling back to a
// deterministic string comparison otherwise (avoids lexicographic surprises
// like "10" < "9").
const compareIds = (a, b) => {
  if (isNumericId(a) && isNumericId(b)) {
    const numA = Number(a);
    const numB = Number(b);
    if (numA !== numB) return numA > numB ? 1 : -1;
    return 0;
  }

  const strA = String(a ?? '');
  const strB = String(b ?? '');

  if (strA === strB) return 0;
  return strA > strB ? 1 : -1;
};

// due_date drives the final sort, most recent first. Missing/invalid dates
// are pushed to the end deterministically instead of comparing as NaN (which
// leaves Array#sort order unspecified).
const getDueDateTime = (order) => {
  const time = new Date(order.due_date).getTime();
  return Number.isNaN(time) ? null : time;
};

const compareDueDates = (a, b) => {
  const timeA = getDueDateTime(a);
  const timeB = getDueDateTime(b);

  if (timeA === null && timeB === null) return 0;
  if (timeA === null) return 1;
  if (timeB === null) return -1;
  return timeB - timeA;
};

export const dedupeSubscriptionOrders = (orders) => {
  const uniqueOrders = new Map();

  orders.forEach((order, index) => {
    // externalId identifies the same charge across retry attempts (mirrors
    // the `externalId || id` convention used elsewhere in this codebase), so
    // it takes priority over the local id. When both are missing, fall back
    // to a key unique to this order instead of a shared `undefined` key, so
    // distinct orders without identifiers are never collapsed into one.
    const uniqueKey = order.externalId ?? order.id ?? Symbol(`missing-order-key-${index}`);

    const existingOrder = uniqueOrders.get(uniqueKey);

    if (!existingOrder) {
      uniqueOrders.set(uniqueKey, order);
    } else {
      const currentOrderDate = new Date(order.createdAt);
      const existingOrderDate = new Date(existingOrder.createdAt);

      if (
        currentOrderDate > existingOrderDate ||
        (currentOrderDate.getTime() === existingOrderDate.getTime() &&
          compareIds(order.id, existingOrder.id) > 0)
      ) {
        uniqueOrders.set(uniqueKey, order);
      }
    }
  });

  return Array.from(uniqueOrders.values()).sort(compareDueDates);
};

const EMPTY_SUBSCRIPTIONS = {
  results: [],
  count: 0,
  active_count: 0,
  active_amount: 0,
  renewed_count: 0,
  renewed_amount: 0,
  ltv: 0,
  mrr: 0,
  churn_rate: 0,
};

const SubscriptionsProvider = ({ children }) => {
  const { user } = useSessionContext();
  const hasMultiCurrencyFeature = hasMultiCurrency(user);

  // Espelha `Subscription.Meta.ordering = ['-createdAt']`: o cabeçalho nasce marcado na ordem
  // que a API já devolve.
  const table = useTableFilter({
    defaultCurrentPage: 1,
    defaultFilter,
    defaultOrderBy: 'date',
    defaultOrder: 'desc',
  });

  const ordering = resolveSubscriptionOrdering(table);

  const [tab, setTab] = useState('active');
  const [currency, setCurrency] = useState(DEFAULT_PRODUCT_CURRENCY_CODE);
  const [createAt, setCreateAt] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const notify = useNotify();

  const buildCreatedAtFilter = (start, end) => {
    const params = new URLSearchParams();

    if (start) {
      const formattedStartDate = start.toISOString().split('T')[0];
      params.append('createdAt__gte', formattedStartDate);
    }

    if (end) {
      const formattedEndDate = `${end.toISOString().split('T')[0]}T23:59:59-03:00`;
      params.append('createdAt__lt', formattedEndDate);
    }

    const queryString = params.toString();
    setCreateAt(queryString);
    return queryString;
  };

  useEffect(() => {
    buildCreatedAtFilter(startDate, endDate);
  }, [startDate, endDate]);

  const {
    data: subscriptions = EMPTY_SUBSCRIPTIONS,
    isFetching: fetchingSubscription,
    refetch: refreshSubscriptions,
    dataUpdatedAt: subscriptionsUpdatedAt,
  } = useQuery({
    queryFn: async () => {
      const rawData = await getSubscription({
        page: table.page,
        limit: table.limit,
        search: table.search,
        createAt,
        ordering,
        ...table.filter,
        ...(hasMultiCurrencyFeature && { currency }),
      });

      if (!rawData?.results) return rawData;

      const filteredResults = rawData.results.map((subscription) => {
        if (!subscription.orders || subscription.orders.length === 0) {
          return subscription;
        }

        return {
          ...subscription,
          orders: dedupeSubscriptionOrders(subscription.orders),
        };
      });

      const filteredData = {
        ...rawData,
        results: filteredResults,
      };

      return filteredData;
    },
    queryKey: [
      'subscription',
      table.filter,
      table.search,
      table.page,
      ordering,
      createAt,
      hasMultiCurrencyFeature,
      currency,
    ],
    placeholderData: EMPTY_SUBSCRIPTIONS,
    staleTime: 600000,
    cacheTime: 1800000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const { data: plans = [], isFetching: fetchingPlans } = useQuery({
    queryFn: () => ({
      plans: [
        { id: '1', name: 'Plano 1' },
        { id: '2', name: 'Plano 2' },
      ],
    }),
    queryKey: ['plans'],
    select: (data) => data.plans.map((plan) => ({ value: plan.id, label: plan.name })),
    initialData: { plans: [] },
    staleTime: 600000,
    cacheTime: 1800000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { mutateAsync: exportSubscription, isLoading: exporting } = useMutation({
    mutationFn: ({ extension }) =>
      exportSuscriptionsService({
        extension,
        page: table.page,
        limit: table.limit,
        ...table.filter,
        ...(hasMultiCurrencyFeature && { currency }),
      }),
    onSuccess: () => notify.success('Exportação realizada com sucesso'),
    onError: () => notify.error('Erro ao realizar exportação'),
  });

  const { mutateAsync: cancel, isLoading: canceling } = useMutation(
    ({ id }) =>
      () =>
        true,
    {
      onSuccess: () =>
        notify.success('Cancelamento realizado com sucesso'),
      onError: () => notify.error('Erro ao realizar cancelamento'),
    }
  );

  const { mutateAsync: deleteSubscription, isLoading: deleting } = useMutation({
    mutationFn: ({ id }) => cancelSybscription({ id }),
    onSuccess: () => {
      notify.success('Assinatura cancelada com sucesso');
      queryClient.invalidateQueries(['subscription']);
    },
    onError: () => notify.error('Erro ao cancelar assinatura'),
  });

  const mrrBreakdown = useMemo(
    () => getSubscriptionMetricBreakdown(subscriptions, 'mrr'),
    [subscriptions]
  );

  const ltvBreakdown = useMemo(
    () => getSubscriptionMetricBreakdown(subscriptions, 'ltv'),
    [subscriptions]
  );
  const value = useMemo(
    () => ({
      table,
      subscriptions,
      fetching: fetchingSubscription,
      fetchingSubscription,
      refreshSubscriptions,
      subscriptionsUpdatedAt,
      plans,
      fetchingPlans,
      cancel,
      canceling,
      exportSubscription,
      exporting,
      deleteSubscription,
      deleting,
      tab,
      setTab,
      activeCount: subscriptions?.active_count ?? 0,
      mrrBreakdown,
      ltvBreakdown,
      currency,
      setCurrency,
      currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
      hasMultiCurrencyFeature,
      buildCreatedAtFilter,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
    }),
    [
      table,
      subscriptions,
      fetchingSubscription,
      refreshSubscriptions,
      subscriptionsUpdatedAt,
      plans,
      fetchingPlans,
      cancel,
      canceling,
      exportSubscription,
      exporting,
      deleteSubscription,
      deleting,
      tab,
      setTab,
      mrrBreakdown,
      ltvBreakdown,
      currency,
      hasMultiCurrencyFeature,
      startDate,
      endDate,
    ]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

SubscriptionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SubscriptionsProvider;
